import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
	fetchRSOs,
	fetchSpaceOrgs,
	clearMatches,
	selectRSOsByContext,
	selectRSOsByIds,
} from "./slice";
import { parseRSOFilters } from "./filters";
import { updateSearchParamsWithFilters } from "../filters/filters";
import Layout from "../../ui/Layout";
import NavMain from "../../app/NavMain";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import RSOTable from "./RSOTable";
import ErrorMsg from "../../ui/ErrorMsg";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import Button from "../../ui/Button";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Header, Overview, Actions, Action } from "../../ui/PageHeader";
import AddPriorityListFromSearchResults from "./AddPriorityListFromSearchResults";
import CreateUserRSO from "./CreateUserRSO";
import Authed from "../iam/Authed";

const PAGE_SIZE = 50; // Default initial and subsequent page size

/* RSOList renders a page containing a filter- and sort-able table
 * of all RSO's matching the criteria in the current URL.*/
const RSOList = () => {
	const dispatch = useDispatch();
	const [isAddingPriorityList, setIsAddingPriorityList] = useState(false);
	const [isAddingRSO, setIsAddingRSO] = useState(false);

	// we get any matching RSO's
	const rsoMatches = useSelector((state) => selectRSOsByContext(state, "root"));
	// We get the full objects from the ids that match
	const rsos = useSelector((state) =>
		selectRSOsByIds(state, rsoMatches?.ids || []),
	);
	// Parse parameters into filter object (which drives filters UI state)
	// Note: We are using the URL as the "state" here, and updates
	// perform a history replacement (so as not to mess up "back" button
	// behaviour too much - though we may want to apply a heuristic like
	// small changes replace history, big changes push history)
	const location = useLocation();
	const navigate = useNavigate();
	const urlParams = new URLSearchParams(location.search);
	const filter = parseRSOFilters(urlParams);

	updateSearchParamsWithFilters(urlParams, filter);

	useEffect(() => {
		dispatch(clearMatches);
		dispatch(
			fetchRSOs({
				filter,
				page: { offset: 0, limit: PAGE_SIZE },
				context: "root",
			}),
		);
		dispatch(fetchSpaceOrgs());
	}, [urlParams.toString()]);

	// How we apply a filter update. Filter state is entirely in the URL query parameters
	const setFilter = (filter = {}) => {
		const updUrlParams = updateSearchParamsWithFilters(urlParams, filter);
		navigate({
			pathname: location.pathname,
			search: `?${updUrlParams}`,
		});
	};

	// How many more RSOs are available to load?
	const loadableCount = Math.min(
		PAGE_SIZE,
		rsos &&
			rsos.length &&
			rsoMatches &&
			rsoMatches.page &&
			rsoMatches.page.total_count
			? rsoMatches.page.total_count - rsos.length
			: 50,
	);

	// Event handler for 'Show xxx more' button that was clicked
	const handleLoadMore = () => {
		const lastIndex = rsos && rsos.length;

		const page = {
			offset: lastIndex,
			limit: loadableCount,
		};
		dispatch(fetchRSOs({ filter, page, context: "root" }));
	};

	const handleAddPriorityList = () => {
		setIsAddingPriorityList(!isAddingPriorityList);
	};

	const handleCloseAddPriorityList = () => {
		setIsAddingPriorityList(false);
	};

	const handleAddRSO = () => {
		setIsAddingRSO(!isAddingRSO);
	};
	const handleCloseAddRSO = () => {
		setIsAddingRSO(false);
	};

	return (
		<Layout>
			<NavMain />
			<Helmet>
				<title>Resident Space Objects - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{rsos.length === 0 && rsoMatches?._loadStatus === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Header>
					<Overview>
						<h1>Resident Space Objects</h1>
						<p>
							{rsos.length > 0 && (
								<span>
									Showing <strong>{rsoMatches?.page?.end_index}</strong>
									{rsoMatches &&
										rsoMatches.page &&
										rsoMatches.page.total_count && (
											<span>
												{" "}
												of{" "}
												<strong className="count total_count">
													{rsoMatches.page.total_count}
												</strong>
											</span>
										)}{" "}
									RSO&apos;s
									{rsoMatches?._loadStatus == "loading" && (
										<span> updating...</span>
									)}
								</span>
							)}
						</p>
					</Overview>
					<Actions>
						<Action>
							<Button onClick={handleAddPriorityList} icon={faPlus}>
								Add to priority list
							</Button>
						</Action>
						<Authed allowRoles={["deployer", "org-admin", "admin"]}>
							<Action>
								<Button onClick={handleAddRSO} icon={faPlus}>
									New RSO from TLE
								</Button>
							</Action>
						</Authed>
					</Actions>
				</Header>
				{rsoMatches?._loadStatus == "rejected" && (
					<ErrorMsg
						msg={"There is a problem fetching the RSOs"}
						showIcon={true}
					/>
				)}
				<Paper>
					<RSOTable rsos={rsos} filter={filter} setFilter={setFilter} />
					{rsos && rsos.length == 0 && rsoMatches?._loadStatus == "idle" && (
						<p>There are no objects matching your criteria.</p>
					)}
					<footer className="actions">
						{rsos.length > 0 && rsos.length !== rsoMatches.page.total_count && (
							<>
								<button
									onClick={handleLoadMore}
									disabled={rsoMatches?._loadStatus == "loading"}
								>
									{rsoMatches?._loadStatus == "loading"
										? "Loading..."
										: `Show ${loadableCount} more`}
								</button>
							</>
						)}
					</footer>
				</Paper>
			</ScrollablePage>
			{isAddingPriorityList && (
				<AddPriorityListFromSearchResults
					totalCount={rsoMatches?.page?.total_count}
					handleCancel={handleCloseAddPriorityList}
				/>
			)}
			{isAddingRSO && <CreateUserRSO handleCancel={handleCloseAddRSO} />}
		</Layout>
	);
};

export default RSOList;
